import { ref } from 'vue';
import { defineStore } from 'pinia';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import {
    IDeltaUserSettings, IFeedVisitState, IUserTrackingData, ResearchSelectionType,
} from '@/interfaces/deltaGpt/IDeltaUserSettings.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { IPaginationOptions } from '@/interfaces/deltaGpt/IPaginationOptions.ts';
import { authState } from '@lcp/http-auth';
import { DEFAULT_SPARK_SITE_SETTINGS, DEFAULT_USER_TRACKING_DATA } from '@/constants/DefaultSparkSiteSettings.ts';
import { SectionRouteNamesResearch } from '@/routes/sectionRoutes/sectionRouteNames.ts';

export const useDeltaUserSettingsStore = defineStore('deltaUserSettingsStore', () => {
    const userSettings = ref<IDeltaUserSettings>(DEFAULT_SPARK_SITE_SETTINGS);
    const userTrackingData = ref<IUserTrackingData>(DEFAULT_USER_TRACKING_DATA);
    const paginationOptions = ref<IPaginationOptions>({
        currentPaginatonRow: 0,
        currentRowNumber: 10,
        isCompactView: false,
    });

    const selectedDocumentFilterPaths = ref<Partial<Record<SectionRouteNamesResearch, Array<string>>>>({});

    const searchFilter = ref<string>('');
    const chatExpanded = ref<boolean>(false);
    const interestAreaPromptDismissedThisSession = ref<boolean>(false);

    function updateShowMyProducts (showMyProducts: boolean): void {
        updateResearchSelection(showMyProducts ? ResearchSelectionType.MyProducts : ResearchSelectionType.AllProducts);
    }

    function updateResearchSelection (researchSelection: ResearchSelectionType): void {
        updateUserSettings({
            researchSelection,
        });
    }

    async function getUserSettings (): Promise<void> {
        return Promise.all([
            deltaGptApi.getUserSettings(),
            deltaGptApi.getUserTrackingData(),
        ]).then(([response, trackingData]) => {
            const responseSettings = response || {};
            Object.entries(responseSettings).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    userSettings.value = {
                        ...userSettings.value,
                        [key]: value,
                    };
                }
            });
            userTrackingData.value = trackingData;
        }).catch(err => {
            throw new Error('Failed to fetch user settings: ', err);
        });
    }

    async function updateUserSettings (settings: Partial<IDeltaUserSettings>): Promise<void> {
        userSettings.value = ({ ...userSettings.value, ...settings }) as IDeltaUserSettings;

        if (authState.value.loggedIn) {
            try {
                await deltaGptApi.updateUserSettings(userSettings.value!);
            } catch (err) {
                console.warn('Failed to update user settings', err);
            }
        }
    }

    async function updateFeedVisitState (settings: Partial<IFeedVisitState>): Promise<void> {
        const feedVisitState: IFeedVisitState = {
            ...userTrackingData.value.feedVisitState,
            ...settings,
            sectionFeedLastVisitedAtByRouteName: {
                ...userTrackingData.value.feedVisitState.sectionFeedLastVisitedAtByRouteName,
                ...settings.sectionFeedLastVisitedAtByRouteName,
            },
        };
        userTrackingData.value = { ...userTrackingData.value, feedVisitState };

        if (authState.value.loggedIn) {
            try {
                await deltaGptApi.updateFeedVisitState(feedVisitState);
            } catch (err) {
                console.warn('Failed to update feed visit state', err);
            }
        }
    }

    async function markYourFeedVisited (): Promise<void> {
        await updateFeedVisitState({ yourFeedLastVisitedAt: new Date().toISOString() });
    }

    async function markAllFeedVisited (): Promise<void> {
        await updateFeedVisitState({ allFeedLastVisitedAt: new Date().toISOString() });
    }

    async function markSectionFeedVisited (sectionRouteName: string): Promise<void> {
        await updateFeedVisitState({
            sectionFeedLastVisitedAtByRouteName: {
                [sectionRouteName]: new Date().toISOString(),
            },
        });
    }

    async function dismissFeedAttention (): Promise<void> {
        await updateFeedVisitState({ attentionDismissedAt: new Date().toISOString() });
    }

    function dismissInterestAreaPromptForSession (): void {
        interestAreaPromptDismissedThisSession.value = true;
    }

    function clearFilters (): void {
        searchFilter.value = '';
    }

    function isDocumentViewed (document: IDocumentSimple): boolean {
        return userTrackingData.value.viewedDocumentShortCodes.includes(document.documentShortCode);
    }

    async function markDocumentViewed (document: IDocumentSimple): Promise<void> {
        if (isDocumentViewed(document)) return;

        userTrackingData.value = {
            ...userTrackingData.value,
            viewedDocumentShortCodes: [...userTrackingData.value.viewedDocumentShortCodes, document.documentShortCode],
        };

        if (authState.value.loggedIn) {
            try {
                await deltaGptApi.addViewedDocumentShortCode(document.documentShortCode);
            } catch (err) {
                console.warn('Failed to mark document as viewed', err);
            }
        }
    }

    return {
        paginationOptions,
        userSettings,
        userTrackingData,
        getUserSettings,
        updateUserSettings,
        searchFilter,
        interestAreaPromptDismissedThisSession,
        updateShowMyProducts,
        updateResearchSelection,
        chatExpanded,
        clearFilters,
        selectedDocumentFilterPaths,
        isDocumentViewed,
        markDocumentViewed,
        markYourFeedVisited,
        markAllFeedVisited,
        markSectionFeedVisited,
        dismissFeedAttention,
        dismissInterestAreaPromptForSession,
    };
});
